import { StyleSheet } from 'react-native';
import React, { useEffect, useState } from 'react';
import {
    ListItem,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Slide,
    Typography,
} from '@mui/material';
import { ColorScheme, theme } from '../../../constants/theme';
import { useThemeContext } from '../../../hook/useThemeManager';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { DiscoveryFilter, FilterItem } from '../../../data/filters/discovery.data';
import { FilterButton } from './FilterButton';

interface IFilterTab {
    data: DiscoveryFilter;
    category: string;
    onChange?: (data: any) => void;
    /** This is triggered when a filter is first toggled. Brings with it the id of the filter group */
    onFilterPressed?: (filterGroupId?: number, extraDetails?: DiscoveryFilter) => void;
    open?: boolean;
    isSolidButton?: boolean;
    onFilterSelect?: (filterItem: FilterItem, categoryId: number) => void;
    defaultValue?: string;
    selectedValue?: string;
}

const FilterTab = React.forwardRef(
    (
        {
            data,
            category,
            onChange,
            onFilterPressed,
            open,
            isSolidButton,
            onFilterSelect,
            defaultValue,
            selectedValue,
        }: IFilterTab,
        ref: any,
    ) => {
        const { themeStyle } = useThemeContext();
        const styles = dynamicStyles(themeStyle);
        const [value, setValue] = useState<string>('');
        const handleDropdown = () => {
            if (onFilterPressed) {
                onFilterPressed(data.id, data);
            }
        };

        useEffect(() => {
            setValue(category);
        }, [category]);

        const isSolidButtonStyle = isSolidButton && {
            backgroundColor: theme[themeStyle].primary_orange,
            '&:hover': { backgroundColor: theme[themeStyle].primary_white },
        };

        const isSolidButtontextStyle = isSolidButton && {
            color: theme[themeStyle].gray_text,
            fontFamily: FONT_ENUM.MAIN_MEDIUM,
            fontSize: 14,
        };

        const mainStyle = { ...styles.button, ...isSolidButtonStyle };
        const textStyle = { ...styles.text, ...isSolidButtontextStyle };

        return (
            <ListItem
                disablePadding
                sx={{
                    width: '100%',
                    height: '40px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'row',
                    position: 'relative',
                    zIndex: 999,
                }}>
                <FilterButton
                    onPress={() => handleDropdown()}
                    value={value}
                    ref={ref}
                    isSolidButton={isSolidButton}
                    mainStyle={mainStyle}
                    textStyle={textStyle}
                    open={open}
                />
                <Slide direction="down" in={open} mountOnEnter unmountOnExit ref={ref}>
                    <FormControl
                        style={styles.dropdown}
                        sx={{
                            border: `1px solid ${theme[themeStyle].highlight}`,
                        }}>
                        <FormLabel
                            style={styles.title}
                            sx={{
                                border: 'none',
                            }}>
                            {data.categoryName}
                        </FormLabel>
                        <RadioGroup
                            value={selectedValue}
                            defaultChecked
                            defaultValue={selectedValue}
                            sx={{
                                maxHeight: '500px',
                                overflowX: 'scroll',
                            }}>
                            {data.data.list.map((tag, i) => {
                                return (
                                    <FormControlLabel
                                        value={tag.displayName}
                                        control={
                                            <Radio
                                                sx={{
                                                    color: theme[themeStyle].gray_message,
                                                    '&.Mui-checked': {
                                                        color: theme[themeStyle].highlight, // Color for checked state
                                                    },
                                                }}
                                            />
                                        }
                                        label={
                                            <Typography
                                                style={{
                                                    fontFamily: FONT_ENUM.MEDIUM_HEADER,
                                                    color: theme[themeStyle].tag_white,
                                                    fontSize: 12,
                                                    textTransform: 'capitalize',
                                                }}>
                                                {tag.displayName}
                                            </Typography>
                                        }
                                        key={i.toString()}
                                        style={styles.text}
                                        onClick={() => onFilterSelect && onFilterSelect(tag, data.id)}
                                        defaultValue={selectedValue}
                                        defaultChecked
                                    />
                                );
                            })}
                        </RadioGroup>
                    </FormControl>
                </Slide>
            </ListItem>
        );
    },
);

export { FilterTab };

const dynamicStyles = (colorScheme: ColorScheme) =>
    StyleSheet.create({
        button: {
            width: '145px',
            flex: 1,
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderRadius: 100,
            paddingBottom: 10,
            paddingTop: 10,
            paddingLeft: 18,
            paddingRight: 18,
            textTransform: 'capitalize',
            backgroundColor: theme[colorScheme].tab_color,
            fontFamily: FONT_ENUM.MAIN_MEDIUM,
        },
        text: {
            width: 'fit-content',
            textAlign: 'left',
            fontSize: 10,
            fontStyle: 'normal',
            fontWeight: '600',
            fontFamily: FONT_ENUM.MAIN_MEDIUM,
            color: theme[colorScheme].filters_white,
        },
        dropdown: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
            width: 'inherit',
            height: 'fit-content',
            padding: '4px',
            position: 'absolute',
            top: 60,
            right: 2,
            paddingBottom: 10,
            paddingTop: 10,
            paddingLeft: 12,
            paddingRight: 12,
            backgroundColor: theme[colorScheme].dropdown_black,
            borderRadius: 8,
        },
        title: {
            width: '100%',
            textAlign: 'left',
            paddingTop: 12,
            paddingBottom: 12,
            paddingLeft: 14,
            paddingRight: 14,
            fontSize: 14,
            fontStyle: 'normal',
            fontWeight: '500',
            fontFamily: FONT_ENUM.MAIN_MEDIUM,
            color: theme[colorScheme].tag_white,
            textTransform: 'capitalize',
        },
    });
